/**
 * Return to lists (plan-implementacion.md §0 and F8).
 *
 * A list remembers the href it was last seen with — its filters and view live
 * in the query — so a detail can send the member back to exactly that list
 * instead of the list's bare canonical URL. Rewritten from R1's
 * `lib/utils/dashboard-navigation.ts` (commit 47d61800), which was tied to the
 * classic `?tab=`/`?sub=` state; on real routes the pathname says which list
 * it is and the query is the list's whole context.
 *
 * Memory only: nothing is persisted, nothing leaves the tab. A reload or a
 * cold entry starts empty and falls back to the canonical list.
 */

import { taskPageOf } from './task-pages'
import {
  destinationForPath,
  destinationHref,
  parseMemberPath,
  toSearchParams,
  type Destination,
  type SearchInput,
} from './routes'

/** Params that describe a visit, not the list: never remembered or restored. */
const TRANSIENT_PARAMS = new Set(['memberId', 'ui', 'viewAs', 'welcome', 'ref', 'fbclid', 'gclid'])
const isTransient = (key: string) => TRANSIENT_PARAMS.has(key) || key.startsWith('utm_')

/** Which list an Encuentro URL is. Null for details and everything else. */
export function listDestinationOf(pathname: string): Destination | null {
  const parsed = parseMemberPath(pathname)
  if (!parsed) return null
  const [section, ...deeper] = parsed.rest
  if (deeper.length > 0) return null
  if (!section) return 'home'
  switch (section) {
    case 'people':
    case 'groups':
    case 'events':
    case 'career':
      return section
    default:
      return null
  }
}

/** Which list a detail URL returns to. Null when it is not a detail. */
export function parentListOf(pathname: string): Destination | null {
  const parsed = parseMemberPath(pathname)
  if (!parsed) return null
  const [section, id, ...deeper] = parsed.rest
  if (!id || deeper.length > 0) return null
  switch (section) {
    case 'profile':
      return 'people'
    case 'groups':
    case 'chapters':
      return 'groups'
    case 'events':
      return 'events'
    case 'jobs':
    case 'opportunities':
      return 'career'
    case 'p':
      return 'home'
    default:
      return null
  }
}

export interface ListSnapshot {
  org: string
  destination: Destination
  /** The list href with its filters and view, minus transient params. */
  href: string
}

/** The snapshot to remember for a visit to a list URL; null for anything else. */
export function listSnapshot(pathname: string, search?: SearchInput): ListSnapshot | null {
  const destination = listDestinationOf(pathname)
  const parsed = parseMemberPath(pathname)
  if (!destination || !parsed) return null
  const params = toSearchParams(search)
  for (const key of [...params.keys()]) if (isTransient(key)) params.delete(key)
  params.sort()
  const path = destination === 'home' ? `/in/${parsed.org}` : `/in/${parsed.org}/${destination}`
  const query = params.toString()
  return { org: parsed.org, destination, href: query ? `${path}?${query}` : path }
}

/**
 * The key a list's scroll position is saved under: one per org, destination,
 * view and filter set, so Groups, Events, the map and another organization
 * never share a position.
 */
export function listScrollKey(snapshot: ListSnapshot | null): string | null {
  return snapshot ? `${snapshot.org}:${snapshot.destination}:${snapshot.href}` : null
}

export type ListReturn = { kind: 'back' } | { kind: 'push'; href: string }

/**
 * How a detail's back control returns to its list:
 * 1. the previous in-app entry IS the remembered list → history back (the
 *    browser restores that entry exactly);
 * 2. a list was visited earlier → push it, filters included, without
 *    resetting scroll;
 * 3. cold entry (mail, push, reload) → push the canonical list.
 */
export function resolveListReturn(input: {
  remembered: string | null
  canonical: string
  previousHref: string | null
}): ListReturn {
  const { remembered, canonical, previousHref } = input
  if (remembered && previousHref === remembered) return { kind: 'back' }
  return { kind: 'push', href: remembered ?? canonical }
}

/**
 * A profile opened from a post, a group, an event or a job: its back link
 * says "Volver" and returns there, instead of always naming People (ALU-241).
 * False on a cold entry (nothing before it) and from People or another profile.
 */
export function cameFromOutsidePeople(previousHref: string | null): boolean {
  if (!previousHref) return false
  const [pathname, search = ''] = previousHref.split('?')
  if (!parseMemberPath(pathname)) return false
  const destination = destinationForPath(pathname, search)
  return destination !== null && destination !== 'people'
}

/** The Encuentro canonical href of a list, for cold entries. */
export function canonicalListHref(org: string, destination: Destination): string {
  return destinationHref(org, destination, 'encuentro')
}

/**
 * How a task page (lib/navigation/task-pages.ts) returns: history back when
 * the page it was opened from is right behind, otherwise a push of that page
 * (or of the task's fallback on a cold entry).
 */
export function resolveTaskReturn(input: {
  origin: string | null
  fallback: string
  previousHref: string | null
}): ListReturn {
  const { origin, fallback, previousHref } = input
  if (origin && previousHref === origin) return { kind: 'back' }
  return { kind: 'push', href: origin ?? fallback }
}

export type ReturnIntent = { refresh: boolean }

export interface ListMemory {
  /** Record a navigation to `href` (pathname + search). */
  visit(pathname: string, search?: SearchInput): void
  /**
   * The page a task page was opened from, recorded on its first visit and
   * kept until the task ends (so Back into a task does not rewrite it).
   */
  originOf(taskPathname: string): string | null
  forgetOrigin(taskPathname: string): void
  /** The next visit to `href` is a return: restore its scroll (and refresh it). */
  markReturn(href: string, intent: ReturnIntent): void
  /** Whether the visit to `href` is a return; `take` also consumes it. */
  peekReturn(href: string): ReturnIntent | null
  takeReturn(href: string): ReturnIntent | null
  /** The remembered href of a list in an org, or null. */
  listHref(org: string, destination: Destination): string | null
  /** The in-app href visited before the current one, normalized like snapshots. */
  previousHref(): string | null
  /** For useSyncExternalStore: called after every visit. */
  subscribe(listener: () => void): () => void
}

export function normalizedHref(pathname: string, search?: SearchInput): string {
  const snapshot = listSnapshot(pathname, search)
  if (snapshot) return snapshot.href
  const query = toSearchParams(search).toString()
  return query ? `${pathname}?${query}` : pathname
}

export function createListMemory(): ListMemory {
  const lists = new Map<string, string>()
  const origins = new Map<string, string>()
  const returns = new Map<string, ReturnIntent>()
  const listeners = new Set<() => void>()
  let current: string | null = null
  let previous: string | null = null

  return {
    visit(pathname, search) {
      const href = normalizedHref(pathname, search)
      if (href !== current) {
        previous = current
        current = href
        const from = previous?.split('?')[0]
        // A task page remembers the page it was opened from, once: Back into
        // the task (or a hop to another task) does not rewrite it.
        if (taskPageOf(pathname) && previous && from && from !== pathname) {
          if (!origins.has(pathname) && !taskPageOf(from)) origins.set(pathname, previous)
        }
      }
      const snapshot = listSnapshot(pathname, search)
      if (snapshot) lists.set(`${snapshot.org}:${snapshot.destination}`, snapshot.href)
      listeners.forEach((listener) => listener())
    },
    listHref: (org, destination) => lists.get(`${org}:${destination}`) ?? null,
    originOf: (taskPathname) => origins.get(taskPathname) ?? null,
    forgetOrigin(taskPathname) {
      origins.delete(taskPathname)
    },
    markReturn(href, intent) {
      const [path, query] = href.split('?')
      returns.set(normalizedHref(path, query ?? ''), intent)
    },
    peekReturn: (href) => returns.get(href) ?? null,
    takeReturn(href) {
      const intent = returns.get(href) ?? null
      returns.delete(href)
      return intent
    },
    previousHref: () => previous,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}
